import _ from 'lodash';
import type { License } from 'types/licenses.types';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';

const licensesPrefix = '/licenses';

// ============ Core License API ============

/**
 * Get all licenses for the current tenant
 */
export const getLicenses = async (
  page?: number,
  limit?: number,
  sortBy?: string,
  sortOrder?: string,
  search?: string,
): Promise<{ licenses: License[]; total: number } | { error: unknown }> => {
  try {
    const params: Record<string, string | number> = {};
    if (page !== undefined) params.page = page;
    if (limit !== undefined) params.limit = limit;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    if (search) params.search = search;
    const response = await API_V1.get(licensesPrefix, { params });
    return {
      total: _.get(response.data, 'total', 0),
      licenses: _.get(response.data, 'licenses', []) as License[],
    };
  } catch (e) {
    Logger.trace('Error while trying to get the available licenses');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Get the full license array for the current user's tenant.
 * Used at sign-in to populate the store.
 */
export const getFullLicense = async (): Promise<License[] | { error: unknown }> => {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[API] Fetching full licenses for current user');
  }
  try {
    const response = await API_V1.get('/licenses/current/full');
    const data = response.data;
    // API returns a flat array
    return Array.isArray(data) ? data : (data.licenses ?? []);
  } catch (e) {
    Logger.trace('Error while getting full licenses');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Get available products and whether the tenant has them.
 */
export const getAvailableProducts = async () => {
  try {
    const response = await API_V1.get('/licenses/current/products');
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting license products');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Cancel a license
 */
export const cancelLicense = async (licenseId: string) => {
  try {
    const res = await API_V1.delete(`${licensesPrefix}/${licenseId}`);
    return res.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while cancelling license %s', licenseId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Delete / cancel a license (alias for backward compat)
 */
export const deleteLicense = cancelLicense;

/**
 * Get license transactions (invoices) from Stripe
 */
export const getLicenseTransactions = async (licenseId: string) => {
  try {
    const response = await API_V1.get(`${licensesPrefix}/${licenseId}/transactions`);
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting license %s info', licenseId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// ============ Admin License CRUD ============

/**
 * Create a new license (admin only).
 * Calls POST /licenses on the new Fastify backend.
 */
export const createLicense = async (body: {
  tenantId: string;
  product: string;
  billingType: 'monthly' | 'quarterly' | 'yearly' | 'one_time';
  credits?: number | null;
  expiresAt?: string | null;
}): Promise<License | { error: unknown }> => {
  try {
    const response = await API_V1.post('/licenses', body);
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating license');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Update an existing license (admin only).
 * Calls PATCH /licenses/:licenseId on the new Fastify backend.
 */
export const editLicense = async (
  licenseId: string,
  body: {
    product?: string;
    billingType?: 'monthly' | 'quarterly' | 'yearly' | 'one_time';
    credits?: number | null;
    expiresAt?: string | null;
    isActive?: boolean;
  },
): Promise<License | { error: unknown }> => {
  try {
    const response = await API_V1.patch(`/licenses/${licenseId}`, body);
    return response.data;
  } catch (e) {
    Logger.trace('Error while updating license %s', licenseId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

// Deprecated – kept as wrapper for backward compatibility
export const getLicensesByUser = async () => getFullLicense();
